import React from 'react';
import { Check, X } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { AVAILABLE_INTEGRATIONS, BASIC_METRICS, ADVANCED_METRICS } from '@/constants/metrics';

interface Plan {
  id: string;
  name: string;
  max_integrations: number;
  integrations_mode: string;
  allowed_integrations: any;
  basic_metrics: any;
  advanced_metrics_enabled: boolean;
  advanced_metrics_mode: string;
  advanced_metrics: any;
  max_advanced_metrics: number;
}

interface PublicPlansComparisonProps {
  plans: Plan[];
}

export function PublicPlansComparison({ plans }: PublicPlansComparisonProps) {
  const isIntegrationIncluded = (plan: Plan, integration: string) => {
    if (plan.integrations_mode === 'customizable') {
      return 'customizable';
    }
    const allowedIntegrations = plan.allowed_integrations as unknown as string[];
    return allowedIntegrations.includes(integration);
  };

  const isBasicMetricIncluded = (plan: Plan, metric: string) => {
    const basicMetrics = plan.basic_metrics as unknown as string[];
    return basicMetrics.includes(metric);
  };

  const isAdvancedMetricIncluded = (plan: Plan, metric: string) => {
    if (!plan.advanced_metrics_enabled) return false;
    if (plan.advanced_metrics_mode === 'customizable') {
      return 'customizable';
    }
    const advancedMetrics = plan.advanced_metrics as unknown as string[];
    return advancedMetrics.includes(metric);
  };

  const renderFeatureCell = (included: boolean | string, plan: Plan, maxCount?: number) => {
    if (included === 'customizable') {
      return (
        <TableCell className="text-center">
          <Badge variant="secondary" className="text-xs">
            Até {maxCount}
          </Badge>
        </TableCell>
      );
    }

    return (
      <TableCell className="text-center">
        {included ? (
          <Check className="h-5 w-5 text-green-600 mx-auto" />
        ) : (
          <X className="h-5 w-5 text-muted-foreground/40 mx-auto" />
        )}
      </TableCell>
    );
  };

  return (
    <div className="border rounded-lg overflow-hidden bg-card">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead className="w-[250px] font-bold text-base sticky left-0 bg-muted/50 z-10">
                Funcionalidades
              </TableHead>
              {plans.map((plan) => (
                <TableHead key={plan.id} className="text-center min-w-[150px]">
                  <span className="font-bold text-base">{plan.name}</span>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {/* LIMITES */}
            <TableRow className="bg-orange-50 dark:bg-orange-950/20">
              <TableCell colSpan={plans.length + 1} className="font-bold text-sm sticky left-0 bg-orange-50 dark:bg-orange-950/20 z-10">
                LIMITES
              </TableCell>
            </TableRow>

            {/* Usuários */}
            <TableRow className="hover:bg-muted/50">
              <TableCell className="font-medium pl-8 sticky left-0 bg-card z-10">
                Usuários
              </TableCell>
              {plans.map((plan) => (
                <TableCell key={plan.id} className="text-center">
                  <Badge variant="secondary" className="text-xs">
                    {(plan as any).max_users === 0 ? 'Ilimitado' : (plan as any).max_users}
                  </Badge>
                </TableCell>
              ))}
            </TableRow>

            {/* Produtos */}
            <TableRow className="hover:bg-muted/50">
              <TableCell className="font-medium pl-8 sticky left-0 bg-card z-10">
                Produtos
              </TableCell>
              {plans.map((plan) => (
                <TableCell key={plan.id} className="text-center">
                  <Badge variant="secondary" className="text-xs">
                    {(plan as any).max_products === 0 ? 'Ilimitado' : (plan as any).max_products}
                  </Badge>
                </TableCell>
              ))}
            </TableRow>

            {/* Integrações */}
            <TableRow className="hover:bg-muted/50">
              <TableCell className="font-medium pl-8 sticky left-0 bg-card z-10">
                Integrações
              </TableCell>
              {plans.map((plan) => (
                <TableCell key={plan.id} className="text-center">
                  <Badge variant="secondary" className="text-xs">
                    {plan.integrations_mode === 'customizable'
                      ? `Até ${plan.max_integrations}`
                      : plan.allowed_integrations.length}
                  </Badge>
                </TableCell>
              ))}
            </TableRow>

            {/* INTEGRAÇÕES */}
            <TableRow className="bg-blue-50 dark:bg-blue-950/20">
              <TableCell colSpan={plans.length + 1} className="font-bold text-sm sticky left-0 bg-blue-50 dark:bg-blue-950/20 z-10">
                INTEGRAÇÕES DISPONÍVEIS
              </TableCell>
            </TableRow>
            {AVAILABLE_INTEGRATIONS.map((integration) => (
              <TableRow key={integration} className="hover:bg-muted/50">
                <TableCell className="font-medium pl-8 sticky left-0 bg-card z-10">
                  {integration}
                </TableCell>
                {plans.map((plan) => {
                  const included = isIntegrationIncluded(plan, integration);
                  return (
                    <React.Fragment key={plan.id}>
                      {renderFeatureCell(included, plan, plan.max_integrations)}
                    </React.Fragment>
                  );
                })}
              </TableRow>
            ))}

            {/* MÉTRICAS BÁSICAS */}
            <TableRow className="bg-green-50 dark:bg-green-950/20">
              <TableCell colSpan={plans.length + 1} className="font-bold text-sm sticky left-0 bg-green-50 dark:bg-green-950/20 z-10">
                MÉTRICAS BÁSICAS
              </TableCell>
            </TableRow>
            {BASIC_METRICS.map((metric) => (
              <TableRow key={metric} className="hover:bg-muted/50">
                <TableCell className="font-medium pl-8 sticky left-0 bg-card z-10">
                  {metric}
                </TableCell>
                {plans.map((plan) => {
                  const included = isBasicMetricIncluded(plan, metric);
                  return (
                    <React.Fragment key={plan.id}>
                      {renderFeatureCell(included, plan)}
                    </React.Fragment>
                  );
                })}
              </TableRow>
            ))}

            {/* MÉTRICAS AVANÇADAS */}
            <TableRow className="bg-purple-50 dark:bg-purple-950/20">
              <TableCell colSpan={plans.length + 1} className="font-bold text-sm sticky left-0 bg-purple-50 dark:bg-purple-950/20 z-10">
                MÉTRICAS AVANÇADAS
              </TableCell>
            </TableRow>
            {ADVANCED_METRICS.map((metric) => (
              <TableRow key={metric} className="hover:bg-muted/50">
                <TableCell className="font-medium pl-8 sticky left-0 bg-card z-10">
                  {metric}
                </TableCell>
                {plans.map((plan) => {
                  const included = isAdvancedMetricIncluded(plan, metric);
                  return (
                    <React.Fragment key={plan.id}>
                      {renderFeatureCell(included, plan, plan.max_advanced_metrics)}
                    </React.Fragment>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
